"use client";

import { capturePostHogException } from "@klarify/posthog";
import { Button } from "@klarify/ui/components/ui/button";
import { Trans } from "@lingui/react/macro";
import Image from "next/image";
import { useEffect } from "react";

export default function RouteError({
	error,
	reset,
}: {
	error: Error & { digest?: string };
	reset: () => void;
}) {
	useEffect(() => {
		if (typeof window !== "undefined") {
			console.error(error);
			// This boundary sits below the layout's <ErrorBoundary>, so it catches
			// most page render errors first and they never reach it. React swallows
			// them and PostHog's capture_exceptions only hooks window.onerror, so
			// without this the app's most common error path reports nothing.
			capturePostHogException(error, { digest: error.digest });
		}
	}, [error]);

	return (
		<div className="flex min-h-dvh w-full items-center justify-center bg-background p-6">
			<div className="flex max-w-md flex-col items-center text-center">
				<Image
					src="/svg/disappointed-klarify-plant.svg"
					alt=""
					width={240}
					height={240}
					className="mb-4"
					priority
				/>
				<h1 className="font-medium text-klarify-neutral-800 text-lg">
					<Trans>Something went wrong</Trans>
				</h1>
				<p className="mt-2 text-klarify-neutral-600 text-sm">
					<Trans>
						We hit an unexpected error loading this page. Try again, and if it
						keeps happening let us know.
					</Trans>
				</p>
				<div className="mt-6 flex flex-col items-center gap-2 sm:flex-row">
					<Button
						onClick={reset}
						variant="default"
					>
						<Trans>Try again</Trans>
					</Button>
					<Button
						asChild
						variant="ghost"
					>
						<a href="/">
							<Trans>Go home</Trans>
						</a>
					</Button>
				</div>
			</div>
		</div>
	);
}
